import type { FlashCard } from "./decks";

/**
 * Shared "Missed from Play" deck. Games append cards here when the
 * player gets something wrong; the Learn tab reads them back.
 *
 * Stored in `localStorage` as a list of FlashCards. We cap the size so
 * the deck stays manageable - older entries are trimmed when adding new
 * ones beyond the cap.
 */

const STORAGE_KEY = "elementpad:learn:missed-from-play:cards";
const MAX_CARDS = 80;

export function loadMissedCards(): FlashCard[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as FlashCard[]) : [];
  } catch {
    return [];
  }
}

function saveMissedCards(cards: FlashCard[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cards));
  } catch {
    /* ignore */
  }
}

/**
 * Append a card. If a card with the same id already exists it is moved
 * to the front (most recently missed first) rather than duplicated.
 */
export function recordMissedCard(card: FlashCard): void {
  const existing = loadMissedCards();
  const without = existing.filter((c) => c.id !== card.id);
  const next = [card, ...without].slice(0, MAX_CARDS);
  saveMissedCards(next);
}

export function clearMissedCards(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Convenience helpers for the two games that feed this deck. */

export function recordMissedElementBySymbol(
  symbol: string,
  name: string,
  atomicNumber: number,
): void {
  recordMissedCard({
    id: `missed-el-sym-${atomicNumber}`,
    prompt: symbol,
    answer: name,
    aliases: [String(atomicNumber)],
    hint: `Atomic number ${atomicNumber}.`,
  });
}

export function recordMissedElectronConfig(
  symbol: string,
  name: string,
  atomicNumber: number,
  config: string,
): void {
  recordMissedCard({
    id: `missed-ecs-${atomicNumber}`,
    prompt: `Electron configuration of ${name} (${symbol})?`,
    answer: config,
    hint: `Z = ${atomicNumber}. Shorthand or full expansion both count.`,
  });
}